import React from 'react';
import Card from '../../components/ui/Card';
import servicesData from '../../data/servicesData.json';
import {
  PhoneIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
  LifebuoyIcon,
  WrenchScrewdriverIcon,
  BoltIcon,
  HomeIcon,
  InformationCircleIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

export default function RepertoirePage() {
  const urgences = [
    {
      titre: "Police Secours",
      numero: "190",
      icon: <ShieldExclamationIcon className="w-6 h-6 text-rose-500" />,
    },
    {
      titre: "Pompiers / Ambulance",
      numero: "150",
      icon: <LifebuoyIcon className="w-6 h-6 text-amber-500" />,
    },
    {
      titre: "Gendarmerie Royale",
      numero: "177",
      icon: <ShieldCheckIcon className="w-6 h-6 text-emerald-500" />,
    },
    {
      titre: "Renseignements",
      numero: "160",
      icon: <InformationCircleIcon className="w-6 h-6 text-sky-500" />,
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-12 animate-fade-in px-4 py-4">
      {/* Header section */}
      <div className="text-center space-y-3 max-w-3xl mx-auto pt-2">
        <span className="px-4 py-1.5 rounded-full text-xs font-extrabold bg-acem-primary/10 text-acem-primary border border-acem-primary/20 backdrop-blur-md uppercase tracking-wider inline-flex items-center gap-1.5">
          <PhoneIcon className="w-4 h-4 text-acem-primary" />
          Contacts Utiles & Services
        </span>
        <h1 className="font-title text-2xl sm:text-3xl md:text-4xl font-extrabold text-acem-primary-dark">
          Répertoire Utile & Contacts
        </h1>
        <p className="text-sm sm:text-base text-acem-muted leading-relaxed max-w-xl mx-auto">
          Numéros d'urgence essentiels et artisans recommandés par la communauté comorienne à Fès.
        </p>
      </div>

      {/* Numéros d'urgence */}
      <section className="space-y-5">
        <div className="flex items-center gap-2.5 border-b border-acem-border pb-3">
          <div className="w-9 h-9 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <LifebuoyIcon className="w-5 h-5" />
          </div>
          <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark">
            Numéros d'Urgence (Maroc)
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {urgences.map((item, idx) => (
            <Card
              key={idx}
              variant="elevated"
              className="p-5 text-center flex flex-col items-center justify-between gap-3 hover:shadow-acem-lg transition-all duration-300 hover:-translate-y-1 rounded-[16px]"
            >
              <div className="w-12 h-12 rounded-2xl bg-acem-surface-container-high border border-acem-border/60 flex items-center justify-center shadow-sm">
                {item.icon}
              </div>
              <div className="space-y-0.5">
                <h3 className="font-bold text-xs sm:text-sm text-acem-text/80">
                  {item.titre}
                </h3>
                <a
                  href={`tel:${item.numero}`}
                  className="font-title text-2xl sm:text-3xl font-extrabold text-acem-primary hover:text-acem-primary-dark transition-colors block"
                >
                  {item.numero}
                </a>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Artisans et professionnels */}
      <section className="space-y-5">
        <div className="flex items-center gap-2.5 border-b border-acem-border pb-3">
          <div className="w-9 h-9 rounded-xl bg-acem-primary/10 text-acem-primary flex items-center justify-center flex-shrink-0 shadow-sm">
            <WrenchScrewdriverIcon className="w-5 h-5" />
          </div>
          <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark">
            Professionnels & Artisans Recommandés
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Plombiers */}
          <Card variant="elevated" className="p-6 space-y-4 rounded-[16px] flex flex-col justify-between">
            <div className="space-y-3">
              <div className="flex items-center gap-2.5 border-b border-acem-border/60 pb-3">
                <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center flex-shrink-0">
                  <WrenchScrewdriverIcon className="w-4 h-4" />
                </div>
                <h3 className="font-title font-bold text-acem-primary-dark text-base">
                  Plombiers
                </h3>
              </div>
              <ul className="divide-y divide-acem-border/50 text-xs sm:text-sm">
                {servicesData.plombiers?.map((item, idx) => (
                  <li key={idx} className="py-2.5 flex justify-between items-center">
                    <span className="font-medium text-acem-text">{item.nom}</span>
                    <a
                      href={`tel:${item.telephone}`}
                      className="text-acem-primary font-bold hover:underline inline-flex items-center gap-1 bg-acem-primary/5 px-2.5 py-1 rounded-full text-xs"
                    >
                      <PhoneIcon className="w-3 h-3" />
                      {item.telephone}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          {/* Électriciens */}
          <Card variant="elevated" className="p-6 space-y-4 rounded-[16px] flex flex-col justify-between">
            <div className="space-y-3">
              <div className="flex items-center gap-2.5 border-b border-acem-border/60 pb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <BoltIcon className="w-4 h-4" />
                </div>
                <h3 className="font-title font-bold text-acem-primary-dark text-base">
                  Électriciens
                </h3>
              </div>
              <ul className="divide-y divide-acem-border/50 text-xs sm:text-sm">
                {servicesData.electriciens?.map((item, idx) => (
                  <li key={idx} className="py-2.5 flex justify-between items-center">
                    <span className="font-medium text-acem-text">{item.nom}</span>
                    <a
                      href={`tel:${item.telephone}`}
                      className="text-acem-primary font-bold hover:underline inline-flex items-center gap-1 bg-acem-primary/5 px-2.5 py-1 rounded-full text-xs"
                    >
                      <PhoneIcon className="w-3 h-3" />
                      {item.telephone}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          {/* Menuisiers */}
          <Card variant="elevated" className="p-6 space-y-4 rounded-[16px] flex flex-col justify-between">
            <div className="space-y-3">
              <div className="flex items-center gap-2.5 border-b border-acem-border/60 pb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <BuildingOfficeIcon className="w-4 h-4" />
                </div>
                <h3 className="font-title font-bold text-acem-primary-dark text-base">
                  Menuisiers
                </h3>
              </div>
              <ul className="divide-y divide-acem-border/50 text-xs sm:text-sm">
                {servicesData.menuisiers?.map((item, idx) => (
                  <li key={idx} className="py-2.5 flex justify-between items-center">
                    <span className="font-medium text-acem-text">{item.nom}</span>
                    <a
                      href={`tel:${item.telephone}`}
                      className="text-acem-primary font-bold hover:underline inline-flex items-center gap-1 bg-acem-primary/5 px-2.5 py-1 rounded-full text-xs"
                    >
                      <PhoneIcon className="w-3 h-3" />
                      {item.telephone}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* Samsars & Logement par quartier */}
      {servicesData.samsars && servicesData.samsars.length > 0 && (
        <section className="space-y-5">
          <div className="flex items-center gap-2.5 border-b border-acem-border pb-3">
            <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <HomeIcon className="w-5 h-5" />
            </div>
            <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark">
              Démarcheurs / Samsars (Logement)
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {servicesData.samsars.map((group, idx) => (
              <Card key={idx} variant="elevated" className="p-6 space-y-3 rounded-[16px]">
                <div className="flex items-center gap-2 border-b border-acem-border/60 pb-3">
                  <HomeIcon className="w-4 h-4 text-acem-primary" />
                  <h3 className="font-title font-bold text-acem-primary-dark text-base">
                    Quartier {group.quartier}
                  </h3>
                </div>
                <ul className="divide-y divide-acem-border/50 text-xs sm:text-sm">
                  {group.contacts.map((c, cIdx) => (
                    <li key={cIdx} className="py-2.5 flex justify-between items-center">
                      <span className="font-medium text-acem-text">{c.nom}</span>
                      <a
                        href={`tel:${c.telephone}`}
                        className="text-acem-primary font-bold hover:underline inline-flex items-center gap-1 bg-acem-primary/5 px-2.5 py-1 rounded-full text-xs"
                      >
                        <PhoneIcon className="w-3 h-3" />
                        {c.telephone}
                      </a>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
